import type { ButtonHTMLAttributes } from "react";
import type { AdminStatusTone } from "@/lib/adminCodeDisplay";
import { adminStatusToneClass, resolveAdminStatusDisplay } from "@/lib/adminCodeDisplay";
import { cn } from "../common/utils";
import AdminButton from "../common/AdminButton";

interface AdminStatusBadgeProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  as?: "span" | "button";
  group?: string;
  code?: string | null;
  label?: string | null;
  shortName?: string | null;
  severity?: string | null;
  tone?: AdminStatusTone;
  className?: string;
}

const AdminStatusBadge = ({
  as = "span",
  group = "DEFAULT",
  code,
  label,
  shortName,
  severity,
  tone,
  className,
  disabled,
  type = "button",
  ...buttonProps
}: AdminStatusBadgeProps) => {
  const display = resolveAdminStatusDisplay({
    group,
    code,
    name: label,
    shortName,
    severity,
    fallbackTone: tone,
  });
  const style = adminStatusToneClass[display.tone];

  const badgeClassName = cn(
    "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
    style.bg,
    style.text,
    style.border,
    as === "button" && "transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60",
    className,
  );
  const content = (
    <>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />
      <span className="min-w-0 truncate">{display.label}</span>
    </>
  );

  if (as === "button") {
    return (
      <AdminButton type={type} variant="plain" size="auto" className={badgeClassName} disabled={disabled} {...buttonProps}>
        {content}
      </AdminButton>
    );
  }

  return (
    <span className={badgeClassName}>
      {content}
    </span>
  );
};

export default AdminStatusBadge;
